import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityVisibilityEnum } from '@sport-activities/sdk';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { FormCheckboxGroupCheckboxInterface } from '~/component/form/checkbox/group/interfaces/checkbox.interface';
import { DropdownOption } from '~/component/form/dropdown/interfaces/option.interface';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { ActivityAgeRangeService } from '~/core/services/activity/age-range/age-range.service';
import { FormControlService } from '~/core/services/form/form-control.service';
import { atLeastOneCheckboxCheckedValidator } from '~/core/validators/at-least-one-checkbox-checked/at-least-one-checkbox-checked.validator';
import { rangeValidator } from '~/core/validators/range/range.validator';
import { AudiencesEnum } from '~/organizer/activity-creation/components/pages/audience/enums/audiences.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { AgeRequirementService } from '~/core/services/activity/age-requirement.service';

@Component({
  selector: 'app-organizer-activity-edition-audience',
  templateUrl: './audience.component.html',
})
export class OrganizerActivityEditionAudienceComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();

  public readonly activityProficiencyLevels =
    appConfig.formValidationRules.activityProficiencyLevels;

  public activity: Activity;
  public audienceOptions: DropdownOption[];
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public maxAgeInputMax = appConfig.formValidationRules.activityMaxAgeInputMax;
  public maxAgeInputMin = appConfig.formValidationRules.activityMaxAgeInputMin;
  public minAgeInputMax = appConfig.formValidationRules.activityMinAgeInputMax;
  public minAgeInputMin = appConfig.formValidationRules.activityMinAgeInputMin;
  public minAgeInputReadonly = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public targetForm: FormGroup;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly formControlService: FormControlService,
    private readonly translateService: TranslateService,
    private readonly activityAgeRangeService: ActivityAgeRangeService,
    private readonly ageRequirementService: AgeRequirementService,
    protected readonly organizerSharedActivityService: OrganizerSharedActivityService,
    protected readonly paramethorService: ParamethorService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.buildAudienceOptions();
    this.targetForm = new FormGroup({
      audience: new FormControl('', Validators.required),
      minAge: new FormControl(0, Validators.required),
      maxAge: new FormControl(0, Validators.required),
      proficiencyLevelAll: new FormControl('', Validators.required),
      proficiencyLevels: new FormControl(this.proficiencyLevelCheckboxes, {
        validators: atLeastOneCheckboxCheckedValidator(),
      }),
      visibility: new FormControl(false),
    });

    this.subscriptions.add(
      this.targetForm.valueChanges.subscribe(() => {
        this.isFormValid = this.targetForm.valid && this.targetForm.dirty;
      })
    );

    this.subscriptions.add(
      this.audience.valueChanges.subscribe(() => this.updateAudienceRange())
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
        this.initFormValues();
      })
    );
  }

  public get audience(): FormControl {
    return this.targetForm.controls.audience as FormControl;
  }

  public get audiences(): { textKey: string; value: string }[] {
    const audience = appConfig.formValidationRules.activityAudience;

    return audience;
  }

  public changeVisibility(): void {
    return this.targetForm.get('visibility').value;
  }

  public get defaultVisibility(): boolean {
    return this.activity.visibility !== 'PUBLIC';
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.proficiencyLevel?.length);
  }

  public get isMaxAgeVisible(): boolean {
    const audience = this.audience.value;

    return (
      audience === AudiencesEnum.Children || audience === AudiencesEnum.Custom
    );
  }

  public get isMinAgeVisible(): boolean {
    const audience = this.audience.value;

    return (
      audience === AudiencesEnum.Children ||
      audience === AudiencesEnum.Adults ||
      audience === AudiencesEnum.Custom
    );
  }

  public get maxAge(): FormControl {
    return this.targetForm.controls.maxAge as FormControl;
  }

  public get minAge(): FormControl {
    return this.targetForm.controls.minAge as FormControl;
  }

  public get proficiencyLevelAll(): FormControl {
    return this.targetForm.controls.proficiencyLevelAll as FormControl;
  }

  public get visibility(): FormControl {
    return this.targetForm.controls.visibility as FormControl;
  }

  public get proficiencyLevelCheckboxes(): FormCheckboxGroupCheckboxInterface[] {
    return this.activityProficiencyLevels.map((proficiencyLevel) => ({
      key: proficiencyLevel.key,
      label: this.translateService.instant(proficiencyLevel.labelKey),
    }));
  }

  public get proficiencyLevels(): FormArray {
    return this.targetForm.controls.proficiencyLevels as FormArray;
  }

  private buildAudienceOptions(): void {
    this.audienceOptions = this.audiences.map(
      (audience): DropdownOption => ({
        disabled: false,
        selected: false,
        text: this.translateService.instant(audience.textKey, {
          minAge: this.ageRequirementService.tenantMajority,
        }),
        value: audience.value,
      })
    );
  }

  private initFormValues(): void {
    this.proficiencyLevelAll.setValue(
      this.activity.proficiencyLevel?.length ===
        this.activityProficiencyLevels?.length
    );

    this.proficiencyLevels.value.map(
      (proficiencyLevel) =>
        (proficiencyLevel.checked = this.activity?.proficiencyLevel?.includes(
          proficiencyLevel.key
        ))
    );

    const minAge =
      this.activity.typicalAgeRange?.min ||
      this.activityAgeRangeService.majorityAge;
    const maxAge =
      this.activity.typicalAgeRange?.max ||
      this.activityAgeRangeService.defaultMaxAge;

    this.audience.setValue(
      this.activityAgeRangeService.getAudienceFromAgeRange(minAge, maxAge)
    );

    this.minAge.setValue(minAge);
    this.maxAge.setValue(maxAge);

    this.visibility.setValue(this.defaultVisibility);
  }

  private updateAudienceRange(): void {
    const audienceRange = this.activityAgeRangeService.getAgeRangeFromAudience(
      this.audience.value
    );
    this.minAgeInputReadonly = audienceRange.disabledMin;
    this.maxAgeInputMax = audienceRange.max;
    this.maxAgeInputMin = audienceRange.min;
    this.minAgeInputMax = audienceRange.max;
    this.minAgeInputMin = audienceRange.min;
    this.maxAge.setValue(audienceRange.max);
    this.minAge.setValue(audienceRange.min);
    this.targetForm.setValidators(
      rangeValidator('minAge', 'maxAge', audienceRange.min, audienceRange.max)
    );
  }

  public errorMessages(inputName: string): string[] {
    const control = this.targetForm.get(inputName);
    const messages = this.formControlService.getErrorMessages(
      control,
      inputName
    );
    return control.touched && messages;
  }

  public updateAudience(): void {
    this.isOverlayDisplayed = true;

    const proficiencyLevels = this.proficiencyLevels.value
      .filter(({ checked }) => checked)
      .map(({ key }) => key);

    const data = {
      proficiencyLevel: proficiencyLevels.length > 0 ? proficiencyLevels : null,
      typicalAgeRange: {
        min: parseInt(this.minAge.value, 10),
        max: parseInt(this.maxAge.value, 10),
      },
      visibility:
        this.visibility.value === true
          ? ('PRIVATE' as ActivityVisibilityEnum)
          : ('PUBLIC' as ActivityVisibilityEnum),
    };

    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, data)
      .pipe(
        mergeMap(() => {
          return this.organizerSharedActivityService
            .getActivity(this.activity?.sku.toString())
            .pipe(
              repeatUntil(
                (activity) =>
                  JSON.stringify({
                    proficiencyLevel: activity.proficiencyLevel,
                    typicalAgeRange: activity.typicalAgeRange,
                    visibility: activity.visibility,
                  }) === JSON.stringify(data),
                3000
              )
            );
        })
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }

  public updateProficiencyLevelAll(): void {
    this.proficiencyLevelAll.setValue(
      this.proficiencyLevels.value.every((value) => value.checked === true)
    );
  }

  public updateProficiencyLevels(): void {
    this.proficiencyLevels.setValue(
      this.proficiencyLevels.value.map((checkbox) => {
        checkbox.checked = this.proficiencyLevelAll.value;

        return checkbox;
      })
    );
  }
}
